// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

// Brand tokens — keep in sync with Reports.css
const TEAL = '#2dd4bf';
const TEAL_DARK = '#14b8a6';
const PURPLE = '#4b4f9e';
const NAVY = '#14141f';
const TEXT_SECONDARY = '#6b7280';
const BORDER_LIGHT = '#e5e7eb';
const BG_SUBTLE = '#f8f9fb';

export const tableHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '16px 22px',
  fontSize: '12px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: NAVY,
  background: BG_SUBTLE,
  borderBottom: `1px solid ${BORDER_LIGHT}`
};

export const tableCellStyle: React.CSSProperties = {
  padding: '16px 22px',
  fontSize: '14px',
  color: NAVY,
  borderBottom: '1px solid #f0f1f3'
};

export const paginationBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '20px'
};

// Pill-shaped secondary button — matches "View Report" styling
export const pageButtonStyle: React.CSSProperties = {
  padding: '9px 20px',
  border: `1.5px solid ${PURPLE}`,
  borderRadius: '999px',
  background: '#fff',
  color: PURPLE,
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer'
};

export const pageButtonDisabledStyle: React.CSSProperties = {
  ...pageButtonStyle,
  opacity: 0.5,
  cursor: 'not-allowed'
};

export const tabBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  borderBottom: `2px solid ${BORDER_LIGHT}`,
  marginBottom: '28px',
  overflowX: 'auto'
};

export const tabStyleBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 18px',
  cursor: 'pointer',
  borderBottom: '2px solid transparent',
  whiteSpace: 'nowrap',
  color: TEXT_SECONDARY,
  fontSize: '14px',
  fontWeight: 600
};

export const tabActiveStyle: React.CSSProperties = {
  ...tabStyleBase,
  borderBottom: `2px solid ${TEAL}`,
  color: NAVY,
  fontWeight: 700
};

export const tabCloseStyle: React.CSSProperties = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: '#888',
  fontSize: '14px',
  lineHeight: 1,
  padding: '2px 4px'
};

export const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: '20px',
  margin: '20px 0 28px',
  flexWrap: 'wrap'
};

export const fieldWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

export const fieldLabelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: NAVY
};

export const dateInputStyle: React.CSSProperties = {
  padding: '10px 14px',
  border: `1px solid ${BORDER_LIGHT}`,
  borderRadius: '10px',
  fontSize: '14px',
  fontFamily: 'inherit',
  color: NAVY,
  background: '#fff'
};

export const searchInputStyle: React.CSSProperties = {
  ...dateInputStyle,
  minWidth: '240px'
};

export const clearFilterButtonStyle: React.CSSProperties = {
  ...pageButtonStyle,
  border: `1px solid ${BORDER_LIGHT}`,
  color: TEXT_SECONDARY
};

// Primary action button — teal gradient pill, matches "Start Assessment"
export const exportButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  borderRadius: '999px',
  border: 'none',
  background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
  color: NAVY,
  fontSize: '14px',
  fontWeight: 700,
  cursor: 'pointer'
};

export const exportButtonDisabledStyle: React.CSSProperties = {
  ...exportButtonStyle,
  opacity: 0.5,
  cursor: 'not-allowed'
};
